/**
 * Semantic search API client.
 *
 * Queries the pgvector-backed semantic_index for provenance nodes
 * matching a natural language query.
 */

import { apiClient } from "./client";

/** Icon type hint for rendering search results by node type. */
export type NodeType =
  | "run" | "geometry" | "surface_mesh" | "volume_mesh"
  | "reference_quantity" | "scalar" | "dataset" | "dataframe"
  | "plot" | "saved_plot" | "file_ref" | "config"
  | "comment" | "branch" | "metadata";

export interface SearchResult {
  node_id: string;
  node_type: NodeType;
  semantic_text: string;
  match_reason?: string;
  score: number;
  // Run-specific fields (populated for node_type === "run")
  name?: string;
  status?: string;
  CL?: number | null;
  CD?: number | null;
  alpha?: number;
  mach?: number;
  geom_name?: string;
  // Provenance / mesh context
  mesh?: string;
  mesh_cell_count?: number;
  // Convergence
  residual?: number;
  iterations?: number;
  // Solver
  turbulence?: string;
  Re?: number;
  // Extra aero
  L_D?: number;
  CM?: number;
  [key: string]: unknown;
}

export interface SearchResponse {
  query: string;
  project_id: string;
  branch: string;
  count: number;
  results: SearchResult[];
}

export interface IndexResponse {
  project_id: string;
  branch: string;
  indexed_count: number;
  forced: boolean;
}

export async function semanticSearch(
  projectId: string,
  query: string,
  branch = "main",
  topK = 10,
  nodeType?: NodeType,
): Promise<SearchResponse> {
  const params = new URLSearchParams({
    project_id: projectId,
    q: query,
    branch,
    top_k: String(topK),
  });
  if (nodeType) {
    params.set("node_type", nodeType);
  }
  return apiClient<SearchResponse>(`/search?${params}`);
}

export async function indexProject(
  projectId: string,
  branch = "main",
  force = false,
): Promise<IndexResponse> {
  const params = new URLSearchParams({
    project_id: projectId,
    branch,
    force: String(force),
  });
  return apiClient<IndexResponse>(`/search/index?${params}`, { method: "POST" });
}
